import { Modal } from 'react-bootstrap';
import { useIntl } from 'react-intl';
import { SaleableProductModel } from '../../b2b-orders';
import { useEffect, useState } from 'react';
import Select from 'react-select';
import { listProductVariants } from '../../product-listing/services/ProductVariantCRUD';
import COUNTRIES from '../../../../app/constants/countries';
import { ProductListingModel } from '../models/ProductListingModel';
import { DistributorModel } from '../../distributors';
import {
  createProductListing,
  updateProductListing,
} from '../services/ProductListingCRUD';
import { toast } from 'react-toastify';
import ProductVariantModel from '../models/ProductVartiant';

interface ProductListingModalFormProps {
  productListing: ProductListingModel | null;
  distributors: DistributorModel[];
  onClose: () => void;
  onUpdated: () => void;
  visible: boolean;
}
export default function ProductListingModalForm({
  distributors,
  productListing = null,
  onClose,
  onUpdated,
  visible,
}: ProductListingModalFormProps) {
  const intl = useIntl();
  const [products, setProducts] = useState<ProductVariantModel[]>([]);
  const productOptions = products.map((product) => ({
    value: product.id,
    label: `${product.name} - ${product.ean}`,
  }));
  const distributorOptions = [
    {
      value: null,
      label: intl.formatMessage({ id: 'productListing.countryDefaultPrice' }),
    },
    ...distributors.map((distributor) => ({
      value: distributor.id,
      label: distributor.name,
    })),
  ];
  const [selectedProduct, setSelectedProduct] =
    useState<ProductVariantModel | null>(null);
  const [country, setCountry] = useState<string | null>(null);
  const [distributor, setDistributor] = useState<number | null>(null);
  const [price, setPrice] = useState<number | null>(null);
  const [pvpr, setPvpr] = useState<number | null>(null);
  const [unitsMultFactor, setUnitsMultFactor] = useState<number | null>(null);
  const [isActive, setIsActive] = useState<boolean>(true);

  async function fetchProducts() {
    try {
      const { data } = await listProductVariants();
      setProducts(data);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  }

  async function onCreateProductListing() {
    try {
      const newProductListing = {
        country,
        distributor,
        product: selectedProduct?.id,
        pvf_unit: price,
        pvpr,
        units_mult_factor: unitsMultFactor,
        is_active: isActive,
      };
      await createProductListing(newProductListing);
      onUpdated();
      toast.success(
        intl.formatMessage({
          id: 'productListing.createProductListingSuccess',
        })
      );
    } catch (error) {
      const errorMessage =
        error?.response?.data?.non_field_errors?.[0] ||
        intl.formatMessage({
          id: 'productListing.createProductListingError',
        });

      toast.error(errorMessage);
    }
  }

  async function onUpdateProductListing() {
    try {
      const updatedProductListing = {
        ...productListing,
        country,
        distributor,
        product: selectedProduct?.id,
        pvf_unit: price,
        pvpr,
        units_mult_factor: unitsMultFactor,
        is_active: isActive,
      };
      await updateProductListing(updatedProductListing);
      onClose();
      onUpdated();
      toast.success(
        intl.formatMessage({
          id: 'productListing.updateProductListingSuccess',
        })
      );
    } catch (error) {
      const errorMessage =
        error?.response?.data?.non_field_errors?.[0] ||
        intl.formatMessage({
          id: 'productListing.updateProductListingError',
        });

      toast.error(errorMessage);
    }
  }

  async function onSubmit() {
    if (productListing?.id) {
      return onUpdateProductListing();
    } else {
      return onCreateProductListing();
    }
  }

  useEffect(() => {
    fetchProducts();
  }, []);

  useEffect(() => {
    if (!visible || !products.length) {
      return;
    }

    if (productListing?.id) {
      setCountry(productListing.country.code);
      setDistributor(productListing?.distributor?.id || null);
      setPrice(productListing.pvf_unit);
      setPvpr(productListing.pvpr);
      setUnitsMultFactor(productListing.units_mult_factor || 8);
      setIsActive(productListing.is_active);

      const existingProduct = products.find(
        (product) =>
          product.id === productListing.product_variant.product_variant_id
      );
      setSelectedProduct(existingProduct || null);
    } else {
      setCountry(null);
      setDistributor(null);
      setPrice(null);
      setPvpr(null);
      setUnitsMultFactor(8);
      setSelectedProduct(null);
      setIsActive(true);
    }
  }, [productListing, visible, products]);

  return (
    <Modal
      show={visible}
      onHide={onClose}
      backdrop="static"
      keyboard={false}
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {productListing?.id
            ? intl.formatMessage({ id: 'productListing.editProductListing' })
            : intl.formatMessage({ id: 'productListing.createProductListing' })}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="d-flex flex-column gap-4">
          <div className="form-group">
            <label htmlFor="country" className="form-label">
              {intl.formatMessage({ id: 'productListing.country' })}
            </label>
            <select
              className="form-select"
              id="country"
              value={country || ''}
              onChange={(e) => setCountry(e.target.value)}
            >
              <option value="" disabled>
                {intl.formatMessage({ id: 'productListing.selectCountry' })}
              </option>
              {COUNTRIES.map((country) => (
                <option key={country} value={country}>
                  {intl.formatMessage({
                    id: `productListing.${country.toLocaleLowerCase()}`,
                  })}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="distributor" className="form-label">
              {intl.formatMessage({ id: 'productListing.distributor' })}
            </label>
            <Select
              className="react-select-container"
              classNamePrefix="react-select"
              options={distributorOptions}
              value={distributorOptions.find(
                (option) => option.value === distributor
              )}
              onChange={(newValue) => setDistributor(newValue.value)}
              placeholder={intl.formatMessage({
                id: 'productListing.allDistributors',
              })}
            />
          </div>
          <div className="form-group">
            <label htmlFor="productName" className="form-label">
              {intl.formatMessage({ id: 'productListing.product' })}
            </label>
            <Select
              className="react-select-container"
              classNamePrefix="react-select"
              options={productOptions}
              value={
                productOptions.find(
                  (option) => option.value === selectedProduct?.id
                ) || null
              }
              onChange={(option) => {
                const product = products.find(
                  (product) => product.id === option.value
                );
                setSelectedProduct(product || null);
              }}
              placeholder={intl.formatMessage({
                id: 'orderDetail.searchProductPlaceholder',
              })}
            />
          </div>
          <div className="form-group">
            <label htmlFor="price" className="form-label">
              {intl.formatMessage({ id: 'productListing.price' })} (€)
            </label>
            <input
              type="number"
              className="form-control"
              step={0.01}
              id="price"
              value={price}
              onChange={(e) =>
                setPrice(
                  e.target.value.length > 0 ? Number(e.target.value) : null
                )
              }
            />
          </div>
          <div className="form-group">
            <label htmlFor="pvpr" className="form-label">
              {intl.formatMessage({ id: 'productListing.pvpr' })} (€)
            </label>
            <input
              type="number"
              className="form-control"
              step={0.01}
              min={0}
              id="pvpr"
              value={pvpr ?? ''}
              onChange={(e) =>
                setPvpr(
                  e.target.value.length > 0 ? Number(e.target.value) : null
                )
              }
            />
          </div>
          <div className="form-group">
            <label htmlFor="unitsMultFactor" className="form-label">
              {intl.formatMessage({ id: 'productListing.unitsMultFactor' })}
            </label>
            <input
              type="number"
              className="form-control"
              step={1}
              id="unitsMultFactor"
              value={unitsMultFactor}
              onChange={(e) =>
                setUnitsMultFactor(
                  e.target.value.length > 0 ? Number(e.target.value) : null
                )
              }
            />
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <button className="btn btn-secondary" onClick={onClose}>
          {intl.formatMessage({ id: 'common.cancel' })}
        </button>
        <button
          onClick={onSubmit}
          className="btn btn-primary"
          disabled={!country || !selectedProduct || price === null}
        >
          {productListing?.id
            ? intl.formatMessage({ id: 'common.save' })
            : intl.formatMessage({ id: 'common.create' })}
        </button>
      </Modal.Footer>
    </Modal>
  );
}
